import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { examsApi, homepageSectionsApi, type HomepageSectionData } from '../../services/api';

const GRADIENT_OPTIONS = [
  { label: 'Emerald', value: 'from-emerald-500 to-teal-600' },
  { label: 'Blue', value: 'from-blue-500 to-indigo-600' },
  { label: 'Purple', value: 'from-purple-500 to-violet-600' },
  { label: 'Orange', value: 'from-orange-500 to-red-500' },
  { label: 'Yellow', value: 'from-yellow-400 to-orange-500' },
  { label: 'Pink', value: 'from-pink-500 to-rose-600' },
  { label: 'Cyan', value: 'from-cyan-400 to-blue-500' },
  { label: 'Lime', value: 'from-lime-400 to-green-500' },
  { label: 'Amber', value: 'from-amber-500 to-orange-600' },
  { label: 'Sky', value: 'from-sky-400 to-blue-600' },
  { label: 'Fuchsia', value: 'from-fuchsia-500 to-pink-600' },
  { label: 'Indigo', value: 'from-indigo-500 to-purple-600' },
  { label: 'Teal', value: 'from-teal-400 to-cyan-600' },
  { label: 'Rose', value: 'from-rose-500 to-red-600' },
  { label: 'Green', value: 'from-green-500 to-emerald-600' },
  { label: 'Violet', value: 'from-violet-500 to-fuchsia-600' },
  { label: 'Red', value: 'from-red-500 to-rose-600' },
  { label: 'Slate', value: 'from-slate-500 to-gray-700' },
  { label: 'Blue Sky', value: 'from-blue-400 to-sky-600' },
  { label: 'Orange Amber', value: 'from-orange-400 to-amber-600' },
];

const LEVEL_OPTIONS = ['Débutant', 'Intermédiaire', 'Avancé', 'Expert'];
const CATEGORY_OPTIONS = ['Cloud Computing', 'Cybersécurité', 'Programmation', 'Base de Données', 'Langues', 'Gestion de Projet', 'DevOps', 'Data Science', 'Scolaire', 'Général'];

interface ExamForm {
  name: string;
  nameEn: string;
  slug: string;
  description: string;
  descriptionEn: string;
  icon: string;
  totalQuestions: number;
  duration: number;
  passingScore: number;
  hasOralQuestions: boolean;
  hasCodeQuestions: boolean;
  hasImageQuestions: boolean;
  hasMathQuestions: boolean;
  isActive: boolean;
  isFeatured: boolean;
  category: string;
  level: string;
  bgGradient: string;
  homepageSectionSlug: string;
}

const defaultForm: ExamForm = {
  name: '',
  nameEn: '',
  slug: '',
  description: '',
  descriptionEn: '',
  icon: '📚',
  totalQuestions: 0,
  duration: 60,
  passingScore: 70,
  hasOralQuestions: false,
  hasCodeQuestions: false,
  hasImageQuestions: false,
  hasMathQuestions: false,
  isActive: true,
  isFeatured: false,
  category: 'Général',
  level: 'Intermédiaire',
  bgGradient: 'from-emerald-500 to-teal-600',
  homepageSectionSlug: '',
};

const inputClass = 'w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

function formatApiError(err: unknown): string {
  const message = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim()) return message;
  return 'Erreur lors de la sauvegarde';
}

function buildExamPayload(form: ExamForm) {
  return {
    name: form.name.trim(),
    nameEn: form.nameEn.trim() || null,
    slug: form.slug.trim(),
    description: form.description.trim(),
    descriptionEn: form.descriptionEn.trim() || null,
    icon: form.icon.trim() || '📚',
    totalQuestions: Number(form.totalQuestions) || 0,
    duration: Number(form.duration) || 60,
    passingScore: Number(form.passingScore) || 70,
    hasOralQuestions: form.hasOralQuestions,
    hasCodeQuestions: form.hasCodeQuestions,
    hasImageQuestions: form.hasImageQuestions,
    hasMathQuestions: form.hasMathQuestions,
    isActive: form.isActive,
    isFeatured: form.isFeatured,
    category: form.category,
    level: form.level,
    bgGradient: form.bgGradient,
    homepageSectionSlug: form.homepageSectionSlug.trim() || null,
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export function ExamEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [form, setForm] = useState<ExamForm>(defaultForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState<HomepageSectionData[]>([]);

  useEffect(() => {
    homepageSectionsApi.getAll()
      .then(({ data }) => setSections(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    examsApi.getById(id)
      .then(({ data }) => {
        setForm({
          name: data.name ?? '',
          nameEn: data.nameEn ?? '',
          slug: data.slug ?? '',
          description: data.description ?? '',
          descriptionEn: data.descriptionEn ?? '',
          icon: data.icon ?? '📚',
          totalQuestions: Number(data.totalQuestions) || 0,
          duration: Number(data.duration) || 60,
          passingScore: Number(data.passingScore) || 70,
          hasOralQuestions: data.hasOralQuestions ?? false,
          hasCodeQuestions: data.hasCodeQuestions ?? false,
          hasImageQuestions: data.hasImageQuestions ?? false,
          hasMathQuestions: data.hasMathQuestions ?? false,
          isActive: data.isActive ?? true,
          isFeatured: data.isFeatured ?? false,
          category: data.category ?? 'Général',
          level: data.level ?? 'Intermédiaire',
          bgGradient: data.bgGradient ?? 'from-emerald-500 to-teal-600',
          homepageSectionSlug: data.homepageSectionSlug ?? '',
        });
      })
      .catch(() => toast.error('Impossible de charger l\'examen'))
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const generateSlug = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const handleNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error('Le nom et le slug sont requis');
      return;
    }
    setSaving(true);
    try {
      const payload = buildExamPayload(form);
      if (isEditing) {
        await examsApi.update(id, payload);
        toast.success('Examen mis à jour');
      } else {
        await examsApi.create(payload);
        toast.success('Examen créé');
      }
      navigate('/admin/exams');
    } catch (err: unknown) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/exams')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Modifier l\'examen' : 'Nouvel examen'}
          </h1>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Nom (français) *">
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="ex: AWS Cloud Practitioner"
            />
          </Field>
          <Field label="Nom (anglais)">
            <input
              type="text"
              className={inputClass}
              value={form.nameEn}
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
              placeholder="e.g. AWS Cloud Practitioner"
            />
          </Field>
        </div>

        <Field label="Slug *">
          <input
            type="text"
            className={inputClass}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="ex: aws-cloud-practitioner"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Description (français)">
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description de l'examen..."
            />
          </Field>
          <Field label="Description (anglais)">
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.descriptionEn}
              onChange={(e) => setForm({ ...form, descriptionEn: e.target.value })}
              placeholder="Exam description..."
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
          <Field label="Icône (emoji)">
            <input
              type="text"
              className={inputClass}
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              placeholder="📚"
            />
          </Field>
          <Field label="Catégorie">
            <select
              className={inputClass}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Niveau">
            <select
              className={inputClass}
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
            >
              {LEVEL_OPTIONS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Grande catégorie (accueil)">
          <select
            className={inputClass}
            value={form.homepageSectionSlug}
            onChange={(e) => setForm({ ...form, homepageSectionSlug: e.target.value })}
          >
            <option value="">— Aucune section —</option>
            {sections.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.icon ? `${s.icon} ` : ''}{s.title}{s.titleEn ? ` / ${s.titleEn}` : ''}
              </option>
            ))}
          </select>
          {sections.length === 0 && (
            <p className="text-xs text-gray-400 mt-1">
              Aucune section configurée — allez dans Paramètres &gt; Sections de l'accueil.
            </p>
          )}
        </Field>

        <div className="grid grid-cols-3 gap-5">
          <Field label="Questions">
            <input type="number" min={0} className={inputClass} value={form.totalQuestions} onChange={(e) => setForm({ ...form, totalQuestions: Number(e.target.value) })} />
          </Field>
          <Field label="Durée (min)">
            <input type="number" min={1} className={inputClass} value={form.duration} onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })} />
          </Field>
          <Field label="Score requis (%)">
            <input type="number" min={0} max={100} className={inputClass} value={form.passingScore} onChange={(e) => setForm({ ...form, passingScore: Number(e.target.value) })} />
          </Field>
        </div>

        <Field label="Couleur (gradient)">
          <div className="flex flex-wrap gap-2 mt-1">
            {GRADIENT_OPTIONS.map((g) => (
              <button
                key={g.value}
                type="button"
                onClick={() => setForm({ ...form, bgGradient: g.value })}
                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${g.value} transition-all ${form.bgGradient === g.value ? 'ring-2 ring-offset-2 ring-emerald-500 scale-110' : ''}`}
                title={g.label}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">{form.bgGradient}</p>
        </Field>

        <Field label="Types de questions">
          <div className="grid grid-cols-2 gap-2 mt-1">
            {[
              { key: 'hasOralQuestions', label: 'Audio / Oral' },
              { key: 'hasCodeQuestions', label: 'Code' },
              { key: 'hasImageQuestions', label: 'Images' },
              { key: 'hasMathQuestions', label: 'Formules Math' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
              </label>
            ))}
          </div>
        </Field>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Actif (visible publiquement)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isFeatured}
              onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Mis en avant</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {saving ? 'Sauvegarde...' : isEditing ? 'Mettre à jour' : 'Créer l\'examen'}
        </button>
      </div>
    </div>
  );
}
